import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import WalletConnect from './WalletConnect';
import './AssetDetail.css';

interface Asset {
  id: string;
  name: string;
  type: string;
  tokenSymbol: string;
  contractAddress?: string;
  chainId?: number;
  totalSupply: string;
}

interface VestingGrant {
  id: string;
  totalUnits: string;
  startDate: string;
  cliffMonths: number;
  durationMonths: number;
  calculation?: {
    vestedUnits: string;
    unvestedUnits: string;
    vestedPercentage: number;
    nextVestDate: string | null;
  };
}

const AssetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [vestingGrants, setVestingGrants] = useState<VestingGrant[]>([]);
  const [distributions, setDistributions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractAddress, setContractAddress] = useState('');
  const [chainId, setChainId] = useState<number>(11155111);
  const [assigningContract, setAssigningContract] = useState(false);

  useEffect(() => {
    if (id) {
      loadAssetData();
    }
  }, [id]);

  const loadAssetData = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [assetData, grantsData, distributionsData] = await Promise.all([
        apiService.getAsset(id),
        apiService.getVestingGrants(),
        apiService.getDistributions(id),
      ]);
      setAsset(assetData);
      setVestingGrants(grantsData.filter((g: any) => g.assetId === id));
      setDistributions(distributionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number, currency: string = 'NZD') => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  };

  const handleAssignContract = async () => {
    if (!id || !contractAddress.trim()) {
      setError('Contract address is required');
      return;
    }

    if (!/^0x[a-fA-F0-9]{40}$/i.test(contractAddress.trim())) {
      setError('Invalid contract address format');
      return;
    }

    setAssigningContract(true);
    setError(null);
    try {
      await apiService.registerContract(id, contractAddress.trim(), chainId);
      setShowContractForm(false);
      setContractAddress('');
      setChainId(11155111);
      await loadAssetData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign contract');
    } finally {
      setAssigningContract(false);
    }
  };

  const isAdmin = state.user?.role === 'ADMIN';

  if (loading) {
    return <div className="asset-detail">Loading...</div>;
  }

  if (!asset) {
    return <div className="asset-detail">Asset not found</div>;
  }

  return (
    <div className="asset-detail">
      <header className="detail-header">
        <div>
          <button onClick={() => navigate(-1)} className="back-button">
            ← Back
          </button>
          <h1>{asset.name}</h1>
          <p>{asset.type} • {asset.tokenSymbol}</p>
        </div>
        <div className="header-actions">
          <WalletConnect />
          <button onClick={async () => { await logout(); navigate('/', { replace: true }); }} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="detail-content">
        <section className="asset-info">
          <div className="section-header-row">
            <h2>Asset Information</h2>
            {isAdmin && !asset.contractAddress && (
              <button
                onClick={() => setShowContractForm(!showContractForm)}
                className="assign-contract-header-button"
              >
                {showContractForm ? 'Cancel' : 'Assign Contract'}
              </button>
            )}
          </div>

          {isAdmin && !asset.contractAddress && showContractForm && (
            <div className="contract-assignment-form">
              <h3>Assign Smart Contract</h3>
              <p className="form-description">
                Link an existing smart contract to this asset. The contract address must be a valid ERC-20 token contract.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label>Contract Address *</label>
                  <input
                    type="text"
                    placeholder="0x..."
                    value={contractAddress}
                    onChange={(e) => setContractAddress(e.target.value)}
                    className="contract-input"
                    disabled={assigningContract}
                  />
                </div>
                <div className="form-group">
                  <label>Chain ID</label>
                  <input
                    type="number"
                    placeholder="11155111"
                    value={chainId}
                    onChange={(e) => setChainId(parseInt(e.target.value) || 11155111)}
                    className="chain-id-input"
                    disabled={assigningContract}
                  />
                  <small className="input-hint">Default: 11155111 (Sepolia)</small>
                </div>
              </div>
              <div className="form-actions">
                <button
                  onClick={handleAssignContract}
                  className="submit-contract-button"
                  disabled={assigningContract || !contractAddress.trim()}
                >
                  {assigningContract ? 'Assigning...' : 'Assign Contract'}
                </button>
                <button
                  onClick={() => {
                    setShowContractForm(false);
                    setContractAddress('');
                    setChainId(11155111);
                    setError(null);
                  }}
                  className="cancel-form-button"
                  disabled={assigningContract}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="info-grid">
            <div className="info-item">
              <label>Name</label>
              <p>{asset.name}</p>
            </div>
            <div className="info-item">
              <label>Type</label>
              <p>{asset.type}</p>
            </div>
            <div className="info-item">
              <label>Token Symbol</label>
              <p>{asset.tokenSymbol}</p>
            </div>
            {asset.contractAddress ? (
              <div className="info-item">
                <label>Contract Address</label>
                <p className="contract-address">{asset.contractAddress}</p>
                {asset.chainId && (
                  <small className="chain-id-display">Chain ID: {asset.chainId}</small>
                )}
              </div>
            ) : (
              <div className="info-item">
                <label>Contract Status</label>
                <p className="no-contract-status">
                  <span className="status-indicator">●</span>
                  No contract assigned
                </p>
                {isAdmin && (
                  <small className="hint-text">Click "Assign Contract" above to link a contract</small>
                )}
              </div>
            )}
          </div>
        </section>

        {vestingGrants.length > 0 && (
          <section className="vesting-section">
            <h2>Vesting Schedule</h2>
            {vestingGrants.map((grant) => (
              <div key={grant.id} className="vesting-card">
                <h3>Grant Details</h3>
                {grant.calculation && (
                  <div className="vesting-stats">
                    <div className="stat">
                      <label>Vested</label>
                      <p>{grant.calculation.vestedUnits}</p>
                    </div>
                    <div className="stat">
                      <label>Unvested</label>
                      <p>{grant.calculation.unvestedUnits}</p>
                    </div>
                    <div className="stat">
                      <label>Progress</label>
                      <p>{grant.calculation.vestedPercentage}%</p>
                    </div>
                  </div>
                )}
                <div className="vesting-details">
                  <p><strong>Total Units:</strong> {grant.totalUnits}</p>
                  <p><strong>Start Date:</strong> {new Date(grant.startDate).toLocaleDateString('en-NZ')}</p>
                  <p><strong>Cliff:</strong> {grant.cliffMonths} months</p>
                  <p><strong>Duration:</strong> {grant.durationMonths} months</p>
                </div>
              </div>
            ))}
          </section>
        )}

        <section className="distributions-section">
          <h2>Distribution History</h2>
          {distributions.length === 0 ? (
            <p>No distributions found.</p>
          ) : (
            <table className="distributions-table">
              <thead>
                <tr>
                  <th>Record Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {distributions.map((distribution) => (
                  <tr key={distribution.id}>
                    <td>{new Date(distribution.recordDate).toLocaleDateString('en-NZ')}</td>
                    <td>{formatCurrency(distribution.amountCents, distribution.currency)}</td>
                    <td>
                      <span className={`status-badge ${distribution.status.toLowerCase()}`}>
                        {distribution.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};

export default AssetDetail;

